import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useFacility } from '@/contexts/FacilityContext';
import { useAuth } from '@/contexts/AuthContext';
import { usePageContext } from '@/hooks/usePageContext';
import { z } from 'zod';

const API = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
export interface Citation { kind: string; label: string; source_table?: string | null; source_id?: string | null; similarity?: number | null; detail?: Record<string, unknown> }
export interface ToolCall { name: string; ok?: boolean; summary?: string }
export interface AgentMessage { id:string; role:'user'|'assistant'; content:string; citations:Citation[]; charts:unknown[]; toolCalls:ToolCall[]; pending?:boolean; error?:boolean; learningRunId?:string }
export interface Conversation { id:string; title:string }
type State = { messages:AgentMessage[]; isStreaming:boolean; loading:boolean; conversationId:string|null; conversations:Conversation[]; error:string|null };
const empty: State = {messages:[],isStreaming:false,loading:false,conversationId:null,conversations:[],error:null};
const states = new Map<string,State>();
const listeners = new Set<()=>void>();
const controllers = new Map<string,AbortController>();
const booting = new Set<string>();
let authGeneration = 0;
const subscribe = (fn:()=>void) => {listeners.add(fn);return ()=>{listeners.delete(fn);};};
const get = (key:string) => states.get(key) || empty;
const put = (key:string, patch:Partial<State>) => {states.set(key,{...get(key),...patch});listeners.forEach(f=>f());};
const storageKey=(key:string)=>`prometheus:conversation:${key}`;

export async function prometheusRequest(path:string, body?:unknown, method?:string) {
  const {data:{session}}=await supabase.auth.getSession();
  if (!session) throw new Error('Sign in to use Prometheus.');
  const response=await fetch(`${API}/prometheus${path}`,{method:method || (body?'POST':'GET'),headers:{Authorization:`Bearer ${session.access_token}`,'Content-Type':'application/json'},body:body?JSON.stringify(body):undefined});
  if (!response.ok) {
    const err=await response.json().catch(()=>({}));
    throw new Error(typeof err.detail==='string'?err.detail:'Prometheus could not complete this request. Please try again.');
  }
  return response.json();
}

const eventSchema=z.discriminatedUnion('type',[
  z.object({type:z.literal('metrics'),total_ms:z.number(),first_text_ms:z.number().nullable(),status:z.string(),spans:z.array(z.object({kind:z.string(),name:z.string(),duration_ms:z.number(),completed:z.boolean(),ok:z.boolean().optional()}))}),
  z.object({type:z.literal('teaching'),learning_run_id:z.string().uuid()}),
  z.object({type:z.literal('start'),conversation_id:z.string()}),
  z.object({type:z.literal('text'),text:z.string()}),
  z.object({type:z.literal('citation'),kind:z.string(),label:z.string(),source_table:z.string().nullish(),source_id:z.string().nullish(),detail:z.record(z.unknown()).optional()}),
  z.object({type:z.literal('chart'),spec:z.unknown()}),
  z.object({type:z.literal('tool_call'),name:z.string()}),
  z.object({type:z.literal('tool_result'),name:z.string(),ok:z.boolean(),summary:z.string()}),
  z.object({type:z.literal('report'),id:z.string(),title:z.string(),summary:z.string()}),
  z.object({type:z.literal('incomplete'),message:z.string()}),
  z.object({type:z.literal('error'),message:z.string()}),
  z.object({type:z.literal('done'),conversation_id:z.string(),status:z.string().optional()}),
]);

async function load(key:string, facilityId:string, id:string) {
  if(get(key).isStreaming) return;
  const generation=authGeneration;
  put(key,{loading:true,error:null,conversationId:id,messages:[]});
  try {
    const data=await prometheusRequest(`/threads/${id}?facility_id=${facilityId}`);
    if(generation!==authGeneration || get(key).conversationId!==id) return;
    const messages:AgentMessage[]=data.turns.flatMap((turn:any)=>[
      {id:`${turn.id}:u`,role:'user',content:turn.message,citations:[],charts:[],toolCalls:[]},
      {id:turn.id,role:'assistant',content:turn.answer || turn.artifacts.find((x:any)=>x.type==='error')?.message || (turn.status==='running'?'This request may still be running. Refresh its saved results before retrying.':'Prometheus could not finish this answer. Check any action receipts below before trying again.'),
        citations:turn.artifacts.filter((x:any)=>x.type==='citation'), charts:turn.artifacts.filter((x:any)=>x.type==='chart').map((x:any)=>x.spec),
        toolCalls:turn.artifacts.filter((x:any)=>x.type==='tool_result'),error:turn.status!=='completed',learningRunId:turn.artifacts.find((x:any)=>x.type==='teaching')?.learning_run_id}]);
    put(key,{messages});localStorage.setItem(storageKey(key),id);
  } catch(err) {if(generation===authGeneration) put(key,{error:err instanceof Error?err.message:'Could not load conversation.'});}
  finally {if(get(key).conversationId===id) put(key,{loading:false});}
}

supabase.auth.onAuthStateChange((event)=>{
  if(event==='SIGNED_OUT') {authGeneration++;controllers.forEach(c=>c.abort());controllers.clear();states.clear();booting.clear();listeners.forEach(f=>f());}
});

export function usePrometheusAgent() {
  const {activeFacility}=useFacility();
  const {profile}=useAuth();
  const page=usePageContext();
  const key=profile?.id && activeFacility?.id ? `${profile.id}:${activeFacility.id}` : '';
  const state=useSyncExternalStore(subscribe,()=>get(key),()=>empty);
  const refresh=useCallback(async()=>{
    if(!key || get(key).isStreaming) return;
    const generation=authGeneration;
    put(key,{loading:true,error:null});
    try {
      const conversations=await prometheusRequest(`/threads?facility_id=${activeFacility!.id}`);
      if(generation!==authGeneration) return;
      put(key,{conversations});
      const saved=get(key).conversationId || localStorage.getItem(storageKey(key));
      if(saved && conversations.some((c:Conversation)=>c.id===saved)) await load(key,activeFacility!.id,saved);
    } catch(err) {if(generation===authGeneration) put(key,{error:err instanceof Error?err.message:'Could not load conversations.'});}
    finally {if(generation===authGeneration) put(key,{loading:false});}
  },[key,activeFacility?.id]);
  useEffect(()=>{
    if(!key || booting.has(key)) return;
    booting.add(key);
    void refresh();
  },[key,refresh]);

  const reset=useCallback(()=>{
    if(get(key).isStreaming) return;
    localStorage.removeItem(storageKey(key));put(key,{messages:[],conversationId:null,error:null});
  },[key]);
  const stop=useCallback(()=>controllers.get(key)?.abort(),[key]);
  const selectConversation=useCallback((id:string)=>load(key,activeFacility!.id,id),[key,activeFacility?.id]);
  const sendMessage=useCallback(async(text:string,_options?:{contextPreamble?:string})=>{
    if(!key) throw new Error('Select a facility to use Prometheus.');
    if(!text.trim() || get(key).isStreaming || get(key).loading) return;
    const generation=authGeneration;
    const requestId=crypto.randomUUID(), assistantId=crypto.randomUUID();
    const controller=new AbortController(); controllers.set(key,controller);
    const base={citations:[],charts:[],toolCalls:[]};
    const conversationId=get(key).conversationId;
    put(key,{isStreaming:true,error:null,messages:[...get(key).messages,{...base,id:requestId,role:'user',content:text.trim()},{...base,id:assistantId,role:'assistant',content:'',pending:true}]});
    const update=(f:(m:AgentMessage)=>AgentMessage)=>generation===authGeneration && put(key,{messages:get(key).messages.map(m=>m.id===assistantId?f(m):m)});
    let terminal=false;
    try {
      const {data:{session}}=await supabase.auth.getSession();
      if(!session) throw new Error('Sign in to use Prometheus.');
      const response=await fetch(`${API}/prometheus/agent`,{method:'POST',headers:{Authorization:`Bearer ${session.access_token}`,'Content-Type':'application/json'},signal:controller.signal,
        body:JSON.stringify({facility_id:activeFacility!.id,conversation_id:conversationId,request_id:requestId,message:text.trim(),page_context:{record:page.record,pathname:page.pathname,module:page.module,timezone:activeFacility!.timezone}})});
      if(!response.ok || !response.body) {const e=await response.json().catch(()=>({}));throw new Error(e.detail || `Request failed (${response.status})`);}
      const reader=response.body.getReader(),decoder=new TextDecoder();let buffer='';
      function frame(raw:string) {
        if(generation!==authGeneration) return;
        const data=raw.split('\n').filter(x=>x.startsWith('data:')).map(x=>x.slice(5).trimStart()).join('\n');
        if(!data) return;
        const parsed=eventSchema.safeParse(JSON.parse(data));
        if(!parsed.success) throw new Error('Prometheus sent an unsupported response. Reopen the conversation to recover saved results.');
        const ev=parsed.data;
        switch(ev.type) {
          case 'teaching':update(m=>({...m,learningRunId:ev.learning_run_id}));break;
          case 'start': put(key,{conversationId:ev.conversation_id});localStorage.setItem(storageKey(key),ev.conversation_id);break;
          case 'text':update(m=>({...m,content:m.content+ev.text}));break;
          case 'citation':update(m=>({...m,citations:[...m.citations,ev as Citation]}));break;
          case 'chart':update(m=>({...m,charts:[...m.charts,ev.spec]}));break;
          case 'tool_call':update(m=>({...m,toolCalls:[...m.toolCalls,{name:ev.name}]}));break;
          case 'tool_result':update(m=>({...m,toolCalls:[...m.toolCalls.filter(t=>t.name!==ev.name),ev]}));break;
          case 'incomplete':update(m=>({...m,content:m.content+'\n\n'+ev.message,error:true}));break;
          case 'error':terminal=true;update(m=>({...m,content:m.content+'\n\n'+ev.message,error:true,pending:false}));break;
          case 'done':terminal=true;update(m=>({...m,pending:false,error:ev.status!=='completed'}));break;
        }
      }
      while(true) {const {done,value}=await reader.read();if(done) break;buffer+=decoder.decode(value,{stream:true});buffer=buffer.replace(/\r\n/g,'\n');let boundary;while((boundary=buffer.indexOf('\n\n'))>=0){frame(buffer.slice(0,boundary));buffer=buffer.slice(boundary+2);}}
      buffer+=decoder.decode();if(buffer.trim()) frame(buffer);
      if(!terminal) throw new Error('Connection ended early. Reopen this conversation to recover saved results before retrying an action.');
    } catch(err) {
      const message=controller.signal.aborted?'Stopped receiving this response. Any action already started may still finish; reopen the conversation to check its receipts.':err instanceof Error?err.message:'Could not reach Prometheus.';
      update(m=>({...m,content:m.content+'\n\n'+message,error:true,pending:false}));
    } finally {
      if(generation!==authGeneration) return;
      put(key,{isStreaming:false});controllers.delete(key);
      void prometheusRequest(`/threads?facility_id=${activeFacility!.id}`).then(conversations=>{if(generation===authGeneration) put(key,{conversations});}).catch(()=>{});
    }
  },[key,activeFacility?.id,page.record,page.pathname,page.module]);
  return {...state,sendMessage,reset,stop,refresh,selectConversation,conversationId:state.conversationId};
}
